import { mutate } from 'swr'
import { putFetcher } from './swr'
import useSWRMutation from 'swr/mutation'
import { useCallback } from 'react'

const isTemplateComponentsKey = (key, tid) =>
  Array.isArray(key) && key[0] === '/components' && key[1]?.tid === tid

/** 后台上传完成后立刻插进列表，避免 60s dedupe 看不到新卡 */
export const optimisticAddComponents = (tid, cpts) => {
  const added = (cpts ?? []).filter(Boolean)
  if (!tid || added.length === 0) return
  return mutate(
    (key) => isTemplateComponentsKey(key, tid),
    (data) => {
      const list = Array.isArray(data) ? data : []
      const exist = new Set(list.map((item) => item.id))
      const fresh = added.filter((item) => item.id && !exist.has(item.id))
      return fresh.length ? [...fresh, ...list] : list
    },
    { revalidate: false },
  )
}

/** 删组件后立刻改 SWR 缓存。全局 dedupingInterval=60s，只 mutate 不改 data 经常刷新不回来。 */
export const optimisticRemoveComponents = (tid, ids) => {
  const idSet = new Set(ids.filter(Boolean))
  if (!tid || idSet.size === 0) return
  return mutate(
    (key) => isTemplateComponentsKey(key, tid),
    (data) => {
      if (!Array.isArray(data)) return data
      return data.filter(
        (item) => !idSet.has(item.id) && !idSet.has(item.pid),
      )
    },
    { revalidate: false },
  )
}

export const optimisticRemoveComponentsByLid = (tid, lid) => {
  if (!tid || !lid) return
  return mutate(
    (key) => isTemplateComponentsKey(key, tid),
    (data) => {
      if (!Array.isArray(data)) return data
      return data.filter((item) => item.lid !== lid)
    },
    { revalidate: false },
  )
}

export const optimisticUpdateAvatars = (id, key, value) => {
  mutate(
    (key) => {
      return Array.isArray(key) && key[0] === '/avatars'
    },
    (data) => {
      return data.map((item) => {
        if (item.id !== id) return item
        if (key === 'addTag') return { ...item, tag: value }
        if (key === 'removeTag') return { ...item, tag: '' }
        return { ...item, [key]: value }
      })
    },
    { revalidate: true },
  )
}

export const optimisticUpdateTemplates = (id, key, value, revalidate) => {
  mutate(
    (key) => {
      return Array.isArray(key) && key[0] === '/templates'
    },
    (data) => {
      return data.map((item) =>
        item.id === id ? { ...item, [key]: value } : item,
      )
    },
    { revalidate },
  )
}

const applyDraftUpdate = (draft, id, key, value) => {
  if (!draft || draft.id !== id) return draft
  const next = JSON.parse(JSON.stringify(draft))
  return draftCase(next, key, value)
}

export const optimisticUpdateDrafts = (id, key, value, revalidate) => {
  mutate(
    [`/drafts/${id}`],
    (data) => applyDraftUpdate(data, id, key, value),
    { revalidate },
  )
  mutate(
    (key) => {
      return Array.isArray(key) && key[0] === '/drafts'
    },
    (data) => {
      return data.map((item) =>
        item.id === id ? applyDraftUpdate(item, id, key, value) : item,
      )
    },
    { revalidate },
  )
}

export const optimisticUpdateDlcDrafts = (id, key, value, revalidate) => {
  mutate(
    [`/dlc/drafts/${id}`],
    (data) => applyDraftUpdate(data, id, key, value),
    { revalidate },
  )
  mutate(
    (key) => {
      return Array.isArray(key) && key[0] === '/dlc/drafts'
    },
    (data) => {
      return data.map((item) =>
        item.id === id ? applyDraftUpdate(item, id, key, value) : item,
      )
    },
    { revalidate },
  )
}

export const optimisticUpdateFrames = (id, key, value, revalidate) => {
  mutate(
    (key) => {
      return Array.isArray(key) && key[0] === '/frames'
    },
    (data) => {
      return data.map((item) =>
        item.id === id ? { ...item, [key]: value } : item,
      )
    },
    { revalidate },
  )
}

const draftCase = (draft, key, value) => {
  if (key === 'removeTag') {
    draft.ext.tags = draft.ext.tags.filter((tag) => tag !== value)
  } else if (key === 'addTag') {
    if (!draft?.ext?.tags) {
      draft.ext.tags = [value]
    } else {
      draft.ext.tags.push(value)
    }
  } else if (key === 'removeLabel') {
    if (draft?.ext?.labels) {
      draft.ext.labels = draft.ext.labels.filter((label) => label !== value)
    }
  } else if (key === 'addLabel') {
    if (!draft?.ext?.labels) {
      draft.ext.labels = [value]
    } else {
      draft.ext.labels.push(value)
    }
  } else if (key === 'removeLang') {
    draft.ext.contentLangs = draft.ext.contentLangs.filter(
      (lang) => lang !== value,
    )
  } else if (key === 'addLang') {
    if (!draft?.ext?.contentLangs) {
      draft.ext.contentLangs = [value]
    } else {
      draft.ext.contentLangs.push(value)
    }
  } else if (key === 'setProductIds') {
    if (!draft.ext) draft.ext = { id: draft.id }
    draft.ext.productIds = Array.isArray(value) ? [...value] : []
  } else if (key === 'removeProduct' || key === 'addProduct') {
    if (!draft.ext) draft.ext = { id: draft.id }
    let next = Array.isArray(draft.ext.productIds)
      ? [...draft.ext.productIds]
      : []
    if (key === 'removeProduct') {
      next = next.filter((pid) => pid !== value)
    } else if (!next.includes(value)) {
      next.push(value)
    }
    draft.ext.productIds = next
  } else if (key === 'level' || key === 'simpleLimit') {
    draft.ext[key] = Number(value)
  } else if (key === 'sku') {
    draft.ext[key] = value
  } else if (key === 'pro' || key === 'iOSPro') {
    const { cid, price } = value
    let layers = JSON.parse(draft.layers).map((layer) => {
      const index = layer.components.findIndex(
        (component) => component.id === cid,
      )
      if (index > -1) layer.components[index][key] = price
      return layer
    })
    draft.layers = JSON.stringify(layers)
  } else {
    draft[key] = value
  }
  return draft
}

export const optimisticUpdateTonings = (id, key, value, revalidate) => {
  mutate(
    (key) => {
      return Array.isArray(key) && key[0] === '/tonings/template'
    },
    (data) => {
      return data.map((item) =>
        item.id === id ? { ...item, [key]: value } : item,
      )
    },
    { revalidate },
  )
}

export const optimisticUpdateDlcs = (id, key, value, revalidate) => {
  mutate(
    (key) => {
      return Array.isArray(key) && key[0] === '/dlcs'
    },
    (data) => {
      return data.map((item) =>
        item.id === id ? dlcCase(item, key, value) : item,
      )
    },
    { revalidate },
  )
}

const dlcCase = (draft, key, value) => {
  if (key === 'removeTag') {
    draft.tags = draft.tags.filter((tag) => tag !== value)
  } else if (key === 'addTag') {
    if (!draft.tags) {
      draft.tags = [value]
    } else {
      draft.tags.push(value)
    }
  } else {
    draft[key] = value
  }
  return draft
}

export const optimisticUpdateLayerIcons = (id, key, value, revalidate) => {
  mutate(
    (key) => {
      return Array.isArray(key) && key[0] === '/layericons'
    },
    (data) => {
      return data.map((item) =>
        item.id === id ? { ...item, [key]: value } : item,
      )
    },
    { revalidate },
  )
}

export const optimisticUpdatePublishers = (id, key, value, revalidate) => {
  mutate(
    (key) => {
      return Array.isArray(key) && key[0] === '/publishers'
    },
    (data) => {
      return data.map((item) =>
        item.id === id ? { ...item, [key]: value } : item,
      )
    },
    { revalidate },
  )
}

export const useUpdateAvatars = (avatarId) => {
  const { trigger, ...swr } = useSWRMutation(`/avatars/${avatarId}`, putFetcher)

  const handleUpdate = useCallback(
    (key, value) => {
      const body = { key, value }
      return trigger(body, {
        optimisticData: (currentData) => {
          optimisticUpdateAvatars(avatarId, key, value)
          return currentData
        },
      })
    },
    [trigger, avatarId],
  )

  return { handleUpdate, trigger, ...swr }
}

export const useUpdateTemplates = (id) => {
  const { trigger, ...swr } = useSWRMutation(`/templates/${id}`, putFetcher)

  const handleUpdate = useCallback(
    (key, value, revalidate) => {
      const body = { key, value }
      return trigger(body, {
        optimisticData: (currentData) => {
          optimisticUpdateTemplates(id, key, value, revalidate)
          return currentData
        },
      })
    },
    [trigger, id],
  )

  return { handleUpdate, trigger, ...swr }
}

export const useUpdateDrafts = (id) => {
  const { trigger, ...swr } = useSWRMutation(`/drafts/${id}`, putFetcher)

  const handleUpdate = useCallback(
    (key, value, revalidate) => {
      const body = { key, value }
      return trigger(body, {
        optimisticData: (currentData) => {
          optimisticUpdateDrafts(id, key, value, revalidate)
          return currentData
        },
      })
    },
    [trigger, id],
  )

  return { handleUpdate, trigger, ...swr }
}

export const useUpdateDlcDrafts = (id) => {
  const { trigger, ...swr } = useSWRMutation(`/dlc/drafts/${id}`, putFetcher)

  const handleUpdate = useCallback(
    (key, value, revalidate) => {
      const body = { key, value }
      return trigger(body, {
        optimisticData: (currentData) => {
          optimisticUpdateDlcDrafts(id, key, value, revalidate)
          return currentData
        },
      })
    },
    [trigger, id],
  )

  return { handleUpdate, trigger, ...swr }
}

export const useUpdateFrames = (id) => {
  const { trigger, ...swr } = useSWRMutation(`/frames/${id}`, putFetcher)

  const handleUpdate = useCallback(
    (key, value, revalidate) => {
      const body = { key, value }
      return trigger(body, {
        optimisticData: (currentData) => {
          optimisticUpdateFrames(id, key, value, revalidate)
          return currentData
        },
      })
    },
    [trigger, id],
  )

  return { handleUpdate, trigger, ...swr }
}

export const useUpdateTonings = (id) => {
  const { trigger, ...swr } = useSWRMutation(
    `/tonings/template/${id}`,
    putFetcher,
  )

  const handleUpdate = useCallback(
    (key, value, revalidate) => {
      const body = { key, value }
      return trigger(body, {
        optimisticData: (currentData) => {
          optimisticUpdateTonings(id, key, value, revalidate)
          return currentData
        },
      })
    },
    [trigger, id],
  )

  return { handleUpdate, trigger, ...swr }
}

export const useUpdateDlcs = (id) => {
  const { trigger, ...swr } = useSWRMutation(`/dlcs/${id}`, putFetcher)

  const handleUpdate = useCallback(
    (key, value, revalidate) => {
      const body = { key, value }
      return trigger(body, {
        optimisticData: (currentData) => {
          optimisticUpdateDlcs(id, key, value, revalidate)
          return currentData
        },
      })
    },
    [trigger, id],
  )

  return { handleUpdate, trigger, ...swr }
}

export const useUpdateLayerIcons = (id) => {
  const { trigger, ...swr } = useSWRMutation(`/layericons/${id}`, putFetcher)

  const handleUpdate = useCallback(
    (key, value, revalidate) => {
      const body = { key, value }
      return trigger(body, {
        optimisticData: (currentData) => {
          optimisticUpdateLayerIcons(id, key, value, revalidate)
          return currentData
        },
      })
    },
    [trigger, id],
  )

  return { handleUpdate, trigger, ...swr }
}

export const useUpdatePublisher = (id) => {
  const { trigger, ...swr } = useSWRMutation(`/publishers/${id}`, putFetcher)

  const handleUpdate = useCallback(
    (key, value, revalidate) => {
      const body = { key, value }
      return trigger(body, {
        optimisticData: (currentData) => {
          optimisticUpdatePublishers(id, key, value, revalidate)
          return currentData
        },
      })
    },
    [trigger, id],
  )

  return { handleUpdate, trigger, ...swr }
}
